import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../api";

const COMPLIANCE_COLORS = {
  "Compliant": "bg-green-50 text-green-700",
  "Expiring Soon": "bg-yellow-50 text-yellow-700",
  "Non-Compliant": "bg-red-50 text-red-700",
  "No Documents": "bg-gray-100 text-gray-500",
};
const APPLICANT_STATUS_COLORS = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Approved: "bg-green-50 text-green-700 border-green-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
};

function CopyLink({ url }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(url).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="text-gray-400 hover:text-gray-700 text-xs"
      title="Copy link"
    >
      {copied ? <span className="text-green-600">✓ copied</span> : "copy"}
    </button>
  );
}

export default function Employees() {
  const location = useLocation();
  // Route still determines the *default* tab on first load, so existing
  // links to /admin/caregivers keep working exactly as before — but from
  // here on, tabs (not the URL) drive what's shown. This keeps everyone's
  // bookmarks and the sidebar link working while merging the destinations
  // into one hub, per the HR-owns-the-whole-lifecycle direction.
  const initialTab = location.pathname.startsWith("/admin/caregivers") ? "field" : "all";
  const [tab, setTab] = useState(initialTab);

  // ---------- Employee data (All / Office / Field Duty tabs) ----------
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // ---------- Applicant data (Applicants tab) ----------
  const [applicants, setApplicants] = useState([]);
  const [jobTypes, setJobTypes] = useState({ field_duty: [], office: [] });
  const [applicantStatusFilter, setApplicantStatusFilter] = useState("All");
  const [showLinks, setShowLinks] = useState(false);
  const [loadingApplicants, setLoadingApplicants] = useState(false);

  useEffect(() => {
    api.employees.list().then(setRows).catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (tab !== "applicants" || applicants.length > 0) return;
    setLoadingApplicants(true);
    Promise.all([api.recruitment.list(), api.recruitment.jobTypes()])
      .then(([apps, jt]) => {
        setApplicants(apps);
        setJobTypes(jt);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingApplicants(false));
  }, [tab]);

  const categoryForTab = tab === "office" ? "Office Staff" : tab === "field" ? "Field Duty Staff" : null;

  const filteredEmployees = rows.filter((r) => {
    const matchesSearch = !search || JSON.stringify(r).toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !categoryForTab || r.staff_category === categoryForTab;
    const matchesStatus = statusFilter === "all" || r.employment_status === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const filteredApplicants = applicants.filter(
    (a) => applicantStatusFilter === "All" || a.status === applicantStatusFilter
  );
  const applicantCounts = { All: applicants.length, New: 0, Approved: 0, Rejected: 0 };
  applicants.forEach((a) => { applicantCounts[a.status] = (applicantCounts[a.status] || 0) + 1; });

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const allJobTypes = [...jobTypes.field_duty, ...jobTypes.office];

  const TABS = [
    { key: "all", label: "All Staff" },
    { key: "office", label: "Office Staff" },
    { key: "field", label: "Field Duty Staff" },
    { key: "applicants", label: `Applicants${applicantCounts.New ? ` (${applicantCounts.New})` : ""}` },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>Employees</h1>
        {tab === "applicants" ? (
          <button
            onClick={() => setShowLinks((s) => !s)}
            className="px-4 py-2 rounded text-sm font-medium border"
            style={{ borderColor: "#154E93", color: "#154E93" }}
          >
            {showLinks ? "Hide" : "Get"} application links
          </button>
        ) : (
          <Link
            to={tab === "field" ? "/admin/employees/new?category=field" : "/admin/employees/new"}
            className="px-4 py-2 text-white rounded text-sm font-medium"
            style={{ backgroundColor: "#154E93" }}
          >
            + Add Employee
          </Link>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Hiring, records, and compliance for everyone on staff — from application to employee record.
      </p>

      <div className="flex gap-2 mb-4 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.key ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={tab === t.key ? { color: "#154E93", borderColor: "#154E93" } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}

      {tab === "applicants" ? (
        <>
          {showLinks && (
            <div className="bg-white rounded-lg shadow-sm p-5 mb-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-1">Shareable application links</h2>
              <p className="text-xs text-gray-400 mb-4">
                Each link pre-selects and locks the position, so you can hand out role-specific links directly.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Field Duty</div>
                  {jobTypes.field_duty.map((job) => {
                    const url = `${origin}/apply?job=${encodeURIComponent(job)}`;
                    return (
                      <div key={job} className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-gray-700">{job}</span>
                        <div className="flex items-center gap-2">
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs" style={{ color: "#154E93" }}>open</a>
                          <CopyLink url={url} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Office / Administrative</div>
                  {jobTypes.office.map((job) => {
                    const url = `${origin}/apply?job=${encodeURIComponent(job)}`;
                    return (
                      <div key={job} className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-gray-700">{job}</span>
                        <div className="flex items-center gap-2">
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs" style={{ color: "#154E93" }}>open</a>
                          <CopyLink url={url} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
                <span className="text-gray-600">General application (no position locked)</span>
                <div className="flex items-center gap-2">
                  <a href={`${origin}/apply`} target="_blank" rel="noopener noreferrer" className="text-xs" style={{ color: "#154E93" }}>open</a>
                  <CopyLink url={`${origin}/apply`} />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2 mb-4">
            {["All", "New", "Approved", "Rejected"].map((s) => (
              <button
                key={s}
                onClick={() => setApplicantStatusFilter(s)}
                className={`text-sm px-3 py-1.5 rounded-full border ${
                  applicantStatusFilter === s ? "text-white" : "text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
                style={applicantStatusFilter === s ? { backgroundColor: "#154E93", borderColor: "#154E93" } : {}}
              >
                {s} {applicantCounts[s] ? `(${applicantCounts[s]})` : ""}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {loadingApplicants ? (
              <div className="p-8 text-center text-sm text-gray-400">Loading applicants…</div>
            ) : filteredApplicants.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400">No applicants in this view yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                    <th className="px-5 py-3">Name</th>
                    <th className="px-5 py-3">Position</th>
                    <th className="px-5 py-3">Phone</th>
                    <th className="px-5 py-3">Applied</th>
                    <th className="px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplicants.map((a) => (
                    <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <Link to={`/admin/recruitment/${a.id}`} className="font-medium hover:underline" style={{ color: "#154E93" }}>
                          {a.first_name} {a.last_name}
                        </Link>
                      </td>
                      <td className="px-5 py-3 text-gray-600">{a.job_type}</td>
                      <td className="px-5 py-3 text-gray-600">{a.phone || "—"}</td>
                      <td className="px-5 py-3 text-gray-500">{a.created_at ? a.created_at.slice(0, 10) : "—"}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${APPLICANT_STATUS_COLORS[a.status] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                          {a.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="flex gap-3 mb-4 flex-wrap">
            <input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 max-w-md border rounded px-3 py-2 text-sm"
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border rounded px-3 py-2 text-sm">
              <option value="all">All statuses</option>
              <option value="Active">Active</option>
              <option value="On Leave">On Leave</option>
              <option value="Suspended">Suspended</option>
              <option value="Terminated">Terminated</option>
            </select>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead style={{ backgroundColor: "#154E93" }}>
                <tr className="text-white text-left">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Employee ID</th>
                  {tab === "all" && <th className="px-4 py-2">Category</th>}
                  <th className="px-4 py-2">Department / Position</th>
                  <th className="px-4 py-2">Status</th>
                  {tab === "field" && <th className="px-4 py-2">Compliance</th>}
                  {tab === "field" && <th className="px-4 py-2">Clients</th>}
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((e) => (
                  <tr key={e.employee_id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{e.first_name} {e.last_name}</td>
                    <td className="px-4 py-2 text-gray-500">{e.employee_id}</td>
                    {tab === "all" && (
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${e.staff_category === "Field Duty Staff" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                          {e.staff_category}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-2">{[e.department, e.position].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="px-4 py-2">{e.employment_status}</td>
                    {tab === "field" && (
                      <td className="px-4 py-2">
                        {e.compliance_status ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COMPLIANCE_COLORS[e.compliance_status] || ""}`}>
                            {e.compliance_status}
                          </span>
                        ) : "—"}
                      </td>
                    )}
                    {tab === "field" && <td className="px-4 py-2">{e.assigned_clients ?? "—"}</td>}
                    <td className="px-4 py-2">
                      <Link to={`/admin/employees/${e.employee_id}`} className="text-blue-600 hover:underline">View</Link>
                    </td>
                  </tr>
                ))}
                {filteredEmployees.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">No records found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
